<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { multiStepRangeSliderRecipe } from './range.recipe';

/**
 * @typedef {object} CsRangeProps
 * @property {number} [value=0] - The current value of the range slider.
 * @property {number} [min=0] - The minimum value of the range slider. (Note: current implementation derives min from the first element of `steps`)
 * @property {number} [max=100] - The maximum value of the range slider. (Note: current implementation derives max from the last element of `steps`)
 * @property {Array<number>} steps - An array of numbers representing the steps in the range. e.g., [5000, 10000, 15000, 20000]
 * @property {Array<string>} labels - An array of strings for labeling the steps. e.g., ['5k', '10k', '15k', '20k']
 * @property {boolean} [disabled=false] - Whether the range slider is disabled.
 */

/**
 * @type {CsRangeProps}
 */
const props = defineProps({
  value: {
    type: Number,
    default: 0,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  steps: {
    type: Array,
    required: true,
  },
  labels: {
    type: Array,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['input', 'change']);

const styles = computed(() =>
  multiStepRangeSliderRecipe({ disabled: props.disabled })
);
const sliderRoot = ref(null);
const thumb = ref(null);
const isDragging = ref(false);
const internalValue = ref(props.value);
const isThumbHovered = ref(false);

const numSteps = computed(() => props.steps.length);

function findClosestStepValue(rawValue) {
  if (props.steps.length === 0) return rawValue;
  let closest = props.steps[0];
  let smallestDiff = Math.abs(rawValue - closest);
  for (let i = 1; i < props.steps.length; i += 1) {
    const diff = Math.abs(rawValue - props.steps[i]);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      closest = props.steps[i];
    } else if (diff === smallestDiff && props.steps[i] < closest) {
      closest = props.steps[i];
    }
  }
  return closest;
}

const percentage = computed(() => {
  // Ensure steps array is not empty and has at least one element
  if (!props.steps || props.steps.length === 0) return 0;

  const overallMin = props.steps[0];
  const overallMax = props.steps[props.steps.length - 1];
  if (overallMax === overallMin) return 0;
  const val = Math.max(overallMin, Math.min(overallMax, internalValue.value));
  return ((val - overallMin) / (overallMax - overallMin)) * 100;
});

const progressStyle = computed(() => ({
  width: `${percentage.value}%`,
  transition: 'width 0.2s ease',
}));

const thumbStyle = computed(() => {
  const isFirstStep = internalValue.value === props.steps[0];
  const isLastStep =
    internalValue.value === props.steps[props.steps.length - 1];
  let transform;
  if (isFirstStep) {
    transform = 'scale(1) translateX(0)';
  } else if (isLastStep) {
    transform = 'scale(1) translateX(-100%)';
  } else {
    transform = 'scale(1) translateX(-10px)';
  }

  return {
    left: `${percentage.value}%`,
    transition: 'left 0.2s ease, transform 0.15s ease-out',
    transform,
  };
});

function getStepPositionStyle(index) {
  if (index === 0 || index === props.steps.length - 1) {
    return { display: 'none' };
  }

  // If only one step, or all steps have the same value, position at 50%.
  // numSteps.value is props.steps.length
  if (numSteps.value === 1) {
    return {
      left: '50%',
      transform: 'translateX(-50%)',
    };
  }

  const overallMin = props.steps[0];
  const overallMax = props.steps[props.steps.length - 1];

  // If all steps are effectively at the same point (e.g. [10, 10, 10])
  if (overallMax === overallMin) {
    return {
      left: '50%',
      transform: 'translateX(-50%)',
    };
  }

  const stepVal = props.steps[index];
  const percent = ((stepVal - overallMin) / (overallMax - overallMin)) * 100;

  return {
    left: `${percent}%`,
    transform: 'translateX(-50%)',
  };
}

function getLabelStepPositionStyle(index) {
  const isFirstStep = index === 0;
  const isLastStep = index === props.steps.length - 1;

  if (numSteps.value === 1) {
    return {
      left: '50%',
      transform: 'translateX(-50%)',
    };
  }

  let transform = 'translateX(-50%)';
  if (isFirstStep) {
    transform = 'translateX(0)';
  } else if (isLastStep) {
    transform = 'translateX(-100%)';
  }

  const overallMin = props.steps[0];
  const overallMax = props.steps[props.steps.length - 1];

  // If all steps are effectively at the same point (e.g. [10, 10, 10])
  if (overallMax === overallMin) {
    return {
      left: '50%',
      transform: 'translateX(-50%)',
    };
  }

  const stepVal = props.steps[index];
  const percent = ((stepVal - overallMin) / (overallMax - overallMin)) * 100;

  return {
    left: `${percent}%`,
    transform,
  };
}

function getStepMarkStyle(index) {
  return getStepPositionStyle(index);
}

function getStepLabelStyle(index) {
  const positionStyle = getLabelStepPositionStyle(index);

  return {
    ...positionStyle,
    position: 'absolute',
  };
}

function updateValueBasedOnPosition(clientX) {
  if (!sliderRoot.value || !props.steps || props.steps.length === 0) return;

  const rect = sliderRoot.value.getBoundingClientRect();
  const x = clientX - rect.left;
  let newPercentage = (x / rect.width) * 100;
  newPercentage = Math.max(0, Math.min(100, newPercentage));

  const overallMin = props.steps[0];
  const overallMax = props.steps[props.steps.length - 1];
  const rawValue =
    overallMin + (newPercentage / 100) * (overallMax - overallMin);

  const newValue = findClosestStepValue(rawValue);

  if (internalValue.value !== newValue) {
    internalValue.value = newValue;
    emit('input', newValue);
    emit('change', newValue);
  }
}

function handleMouseDown(event) {
  if (props.disabled) return;
  isDragging.value = true;
  updateValueBasedOnPosition(event.clientX);
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
  thumb.value?.focus();
}

function handleMouseMove(event) {
  if (props.disabled || !isDragging.value) return;
  updateValueBasedOnPosition(event.clientX);
}

function handleMouseUp() {
  if (props.disabled) return;
  isDragging.value = false;
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
}

function handleTouchStart(event) {
  if (props.disabled) return;
  isDragging.value = true;
  updateValueBasedOnPosition(event.touches[0].clientX);
  document.addEventListener('touchmove', handleTouchMove);
  document.addEventListener('touchend', handleTouchEnd);
  thumb.value?.focus();
}

function handleTouchMove(event) {
  if (props.disabled || !isDragging.value) return;
  updateValueBasedOnPosition(event.touches[0].clientX);
}

function handleTouchEnd() {
  if (props.disabled) return;
  isDragging.value = false;
  document.removeEventListener('touchmove', handleTouchMove);
  document.removeEventListener('touchend', handleTouchEnd);
}

function changeStep(direction) {
  if (!props.steps || props.steps.length === 0) return;
  const currentIndex = props.steps.indexOf(internalValue.value);
  let nextIndex = currentIndex + direction;
  nextIndex = Math.max(0, Math.min(props.steps.length - 1, nextIndex));

  if (
    props.steps[nextIndex] !== undefined &&
    internalValue.value !== props.steps[nextIndex]
  ) {
    internalValue.value = props.steps[nextIndex];
    emit('input', internalValue.value);
    emit('change', internalValue.value);
  }
}

function handleKeyDown(event) {
  if (props.disabled || !props.steps || props.steps.length === 0) return;
  switch (event.key) {
    case 'ArrowLeft':
    case 'ArrowDown':
      event.preventDefault();
      changeStep(-1);
      break;
    case 'ArrowRight':
    case 'ArrowUp':
      event.preventDefault();
      changeStep(1);
      break;
    case 'Home':
      event.preventDefault();
      if (props.steps.length > 0) {
        internalValue.value = props.steps[0];
        emit('input', internalValue.value);
        emit('change', internalValue.value);
      }
      break;
    case 'End':
      event.preventDefault();
      if (props.steps.length > 0) {
        internalValue.value = props.steps[props.steps.length - 1];
        emit('input', internalValue.value);
        emit('change', internalValue.value);
      }
      break;
    default:
      break;
  }
}

function handleThumbMouseEnter() {
  if (!props.disabled) {
    isThumbHovered.value = true;
  }
}

function handleThumbMouseLeave() {
  isThumbHovered.value = false;
}

watch(
  () => props.value,
  newValue => {
    if (!props.steps || props.steps.length === 0) {
      // If steps are not defined, perhaps value should directly be used
      // or a default behavior. For now, let's prevent error if findClosestStepValue is called with empty steps.
      if (props.steps.length === 0) {
        internalValue.value = newValue; // Or some other handling
        return;
      }
    }
    const closestStepVal = findClosestStepValue(newValue);
    if (internalValue.value !== closestStepVal) {
      internalValue.value = closestStepVal;
    }
  }
);

// Initialize internalValue to the closest step of value
if (props.steps && props.steps.length > 0) {
  internalValue.value = findClosestStepValue(props.value);
  if (internalValue.value !== props.value) {
    // Ensure parent syncs if initial value isn't a perfect step
    // and steps are available to determine the closest step
    emit('input', internalValue.value);
  }
} else {
  internalValue.value = props.value; // Fallback if steps are not defined
}

onMounted(() => {
  // Ensure initial position is correct if value is set
  if (props.steps && props.steps.length > 0) {
    const closestStepVal = findClosestStepValue(props.value);
    if (internalValue.value !== closestStepVal) {
      internalValue.value = closestStepVal;
      // No emit here as watcher should handle or it's initial state,
      // or the initial emit above already handled it.
    }
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
  document.removeEventListener('touchmove', handleTouchMove);
  document.removeEventListener('touchend', handleTouchEnd);
});
</script>

<template>
  <div
    ref="sliderRoot"
    data-component="CsRangeMultistep"
    :class="styles.root({ disabled })"
    @mousedown.prevent="!disabled && handleMouseDown($event)"
    @touchstart.prevent="!disabled && handleTouchStart($event)"
  >
    <div :class="styles.track()">
      <div :class="styles.progress()" :style="progressStyle" />
      <div
        v-for="(label, index) in labels"
        :key="`mark-${index}`"
        :class="styles.stepMark()"
        :style="getStepMarkStyle(index)"
      />
      <div
        ref="thumb"
        :class="styles.thumb()"
        :style="thumbStyle"
        tabindex="0"
        @keydown="!disabled && handleKeyDown($event)"
        @mouseenter="handleThumbMouseEnter"
        @mouseleave="handleThumbMouseLeave"
      />
    </div>
    <div :class="styles.stepsContainer()">
      <span
        v-for="(label, index) in labels"
        :key="`label-${index}`"
        :class="styles.stepLabel()"
        :style="getStepLabelStyle(index)"
      >
        {{ label }}
      </span>
    </div>
  </div>
</template>
